<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import api from '@/services/api';
import { generarReportePDF, generarReporteCSV } from '@/js/Reportes.js';
import { imprimirEtiquetasMasivas } from '@/js/ImpresionMasiva.js';
import logo from "@/assets/img/fondotam-blanco.png";

const exportando = ref(false);
const loading = ref(false);
const imprimiendoEtiquetas = ref(false);
const busquedaRealizada = ref(false);
const activos = ref([]);
const totalResultados = ref(0);

// Filtros actualizados
const filtros = ref({
  q: '',
  codigo_tamep: '',
  codigo_tab: '',
  tiene_tamep: '',
  tiene_tab: '',
  regional: '',
  gerencia: '',
  seccion: '',
  cargo: '',
  usuario: '',
  grupo_contable: '',
  tipo_activo: '',
  situacion: '',
  estado: '',
  perfil_uso: '',
});

// Datos para selects
const regionales = ref([]);
const gruposContables = ref([]);
const tiposActivo = ref([]);

const opciones = reactive({ gerencias: [], secciones: [], cargos: [], usuarios: [] });
const listaPersonal = ref([]);

const cargarParametros = async () => {
  loading.value = true;
  try {
    const [resRegionales, resGrupos, resPers] = await Promise.all([
      api.get('regionales/'),
      api.get('grupos-contables/'),
      api.get('activos/personal_list/')
    ]);
    regionales.value = resRegionales.data.results || resRegionales.data;
    gruposContables.value = resGrupos.data.results || resGrupos.data;
    listaPersonal.value = resPers.data;
  } catch (error) {
    console.error("Error al cargar parámetros:", error);
  } finally {
    loading.value = false;
  }
};

watch(() => filtros.value.grupo_contable, async (nuevoGrupo) => {
  tiposActivo.value = [];
  filtros.value.tipo_activo = '';
  if (nuevoGrupo) {
    try {
      const res = await api.get(`tipos-activos/?grupo_contable=${nuevoGrupo}`);
      tiposActivo.value = res.data.results || res.data;
      buscar(); // Disparamos búsqueda al cambiar
    } catch (e) { /* silenciar */ }
  } else if (busquedaRealizada.value) {
    buscar();
  }
});

const buscar = async () => {
  busquedaRealizada.value = true;
  loading.value = true;
  try {
    const params = new URLSearchParams();

    // Búsqueda de texto general
    if (filtros.value.q) params.append('search', filtros.value.q);

    // NUEVO: Filtros de Existencia de Códigos (Booleanos)
    if (filtros.value.tiene_tamep !== '') params.append('tiene_tamep', filtros.value.tiene_tamep);
    if (filtros.value.tiene_tab !== '') params.append('tiene_tab', filtros.value.tiene_tab);

    // Filtros de categoría
    if (filtros.value.regional) params.append('regional', filtros.value.regional);
    if (filtros.value.gerencia) params.append('gerencia', filtros.value.gerencia);
    if (filtros.value.seccion) params.append('seccion', filtros.value.seccion);
    if (filtros.value.cargo) params.append('cargo', filtros.value.cargo);
    if (filtros.value.usuario) params.append('usuario', filtros.value.usuario);
    if (filtros.value.grupo_contable) params.append('grupo_contable_seleccionado', filtros.value.grupo_contable);
    if (filtros.value.tipo_activo) params.append('tipo_activo', filtros.value.tipo_activo);
    if (filtros.value.situacion) params.append('situacion_activo', filtros.value.situacion);
    if (filtros.value.estado) params.append('estado_actual', filtros.value.estado);

    const res = await api.get(`gestion-activos/?${params.toString()}`);
    activos.value = res.data.results || res.data;
    totalResultados.value = res.data.count || activos.value.length;
  } catch (error) {
    console.error("Error en búsqueda:", error);
  } finally {
    loading.value = false;
  }
};

const limpiarFiltros = () => {
  filtros.value = {
    q: '', tiene_tamep: '', tiene_tab: '', regional: '', gerencia: '', seccion: '', cargo: '', usuario: '',
    grupo_contable: '', tipo_activo: '', situacion: '', estado: ''
  };
  opciones.gerencias = [];
  opciones.secciones = [];
  opciones.cargos = [];
  opciones.usuarios = [];
  tiposActivo.value = [];
  activos.value = [];
  totalResultados.value = 0;
  busquedaRealizada.value = false;
};

const exportarPDF = () => {
  const responsableSeleccionado = opciones.usuarios.find(u => u.id == filtros.value.usuario);
  const cargoSeleccionado       = opciones.cargos.find(c => c.id == filtros.value.cargo);
  const regionalSeleccionada    = regionales.value.find(r => r.id == filtros.value.regional);
  const gerenciaSeleccionada    = opciones.gerencias.find(g => g.id == filtros.value.gerencia);
  const seccionSeleccionada     = opciones.secciones.find(s => s.id == filtros.value.seccion);

  // Determinar si hay un único responsable en los filtros o en los activos devueltos
  let responsableNombre = responsableSeleccionado?.nombre_completo || '';
  if (!responsableNombre && activos.value.length > 0) {
    const custodiosUnicos = [...new Set(
      activos.value
        .map(a => a.custodio_nombre || a.responsable_nombre)
        .filter(c => c && c !== 'Sin asignar' && c !== 'No Asignado')
    )];
    if (custodiosUnicos.length === 1) {
      responsableNombre = custodiosUnicos[0];
    }
  }

  const datosAsignacion = {
    responsable: responsableNombre,
    cargo:       cargoSeleccionado?.nombre || '',
    oficina:     regionalSeleccionada?.nombre || '',
    gerencia:    gerenciaSeleccionada?.nombre || '',
    ciudad:      regionalSeleccionada?.nombre || '',
    unidad:      seccionSeleccionada?.nombre || gerenciaSeleccionada?.nombre || '',
    entregadoPor: '',
    nroActa:     '',
    fechaRecepcion: new Date().toLocaleDateString('es-BO')
  };

  generarReportePDF(activos.value, datosAsignacion, (val) => exportando.value = val);
};

const exportarCSV = () => {
  generarReporteCSV(activos.value);
};

const imprimirEtiquetas = async () => {
  imprimiendoEtiquetas.value = true;
  try {
    await imprimirEtiquetasMasivas(activos.value);
  } finally {
    imprimiendoEtiquetas.value = false;
  }
};

watch(() => filtros.value.regional, async (newVal) => {
  if (newVal) {
    try {
      const { data } = await api.get(`gerencias/?regional=${newVal}`);
      opciones.gerencias = data;
    } catch (e) { console.error(e); }
  } else {
    opciones.gerencias = [];
  }
  filtros.value.gerencia = '';
  filtros.value.seccion = '';
  filtros.value.cargo = '';
  filtros.value.usuario = '';
  opciones.secciones = [];
  opciones.cargos = [];
  opciones.usuarios = [];
  buscar();
});

watch(() => filtros.value.gerencia, async (newVal) => {
  if (newVal) {
    try {
      const { data } = await api.get(`secciones/?gerencia=${newVal}`);
      opciones.secciones = data;
    } catch (e) { console.error(e); }
  } else {
    opciones.secciones = [];
  }
  filtros.value.seccion = '';
  filtros.value.cargo = '';
  filtros.value.usuario = '';
  opciones.cargos = [];
  opciones.usuarios = [];
  buscar();
});

watch(() => filtros.value.seccion, async (newVal) => {
  if (newVal) {
    try {
      const { data } = await api.get(`cargos/?seccion=${newVal}`);
      opciones.cargos = data;
    } catch (e) { console.error(e); }
  } else {
    opciones.cargos = [];
  }
  filtros.value.cargo = '';
  filtros.value.usuario = '';
  opciones.usuarios = [];
  buscar();
});

watch(() => filtros.value.cargo, (newVal) => {
  if (newVal) {
    opciones.usuarios = listaPersonal.value.filter(p =>
      p.cargo_id == newVal &&
      p.seccion_id == filtros.value.seccion &&
      p.gerencia_id == filtros.value.gerencia &&
      p.regional_id == filtros.value.regional
    );
  } else {
    opciones.usuarios = [];
  }
  filtros.value.usuario = '';
  buscar();
});

watch(() => filtros.value.usuario, () => buscar());

const badgeSituacion = (sit) => {
  const map = {
    'Disponible': 'bg-gradient-info',
    'Asignado': 'bg-gradient-success',
    'Mantenimiento': 'bg-gradient-warning',
    'Baja': 'bg-gradient-danger'
  };
  return map[sit] || 'bg-secondary';
};

onMounted(async () => {
  await cargarParametros();
});
</script>

<template>
  <div class="container-fluid py-4 app-transition">
    <div class="card shadow-lg border-radius-xl card-custom overflow-hidden">
      <div class="header-tamep px-4 py-3 d-flex justify-content-between align-items-center flex-wrap border-bottom">
        <div class="d-flex align-items-center mb-sm-0 mb-3">
          <div class="logo-container-left me-3 p-1 rounded-lg shadow-sm"
            style="border-radius: 8px; background-color: #ffffff !important;">
            <img :src="logo" class="logo-img p-1" style="max-height: 40px;">
          </div>
          <div class="border-start ps-3 border-white opacity-8">
            <h6 class="text-white font-weight-bolder text-uppercase mb-0">Centro de Reportes</h6>
            <small class="text-white opacity-8">Consulta avanzada y exportación de inventario</small>
          </div>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="btn btn-sm mb-0 shadow-sm font-weight-bolder btn-danger" @click="exportarPDF"
            :disabled="exportando">
            <i class="fas fa-file-pdf me-1"></i> Exportar PDF
          </button>
          <button class="btn btn-sm mb-0 shadow-sm font-weight-bolder btn-success" @click="exportarCSV">
            <i class="fas fa-file-csv me-1"></i> Exportar CSV
          </button>
          <button
            class="btn btn-sm mb-0 shadow-sm font-weight-bolder"
            style="background:#ffffff;color:#003366;border:1px solid rgba(255,255,255,0.5);"
            @click="imprimirEtiquetas"
            :disabled="imprimiendoEtiquetas || !activos.length"
            :title="`Imprimir etiquetas de ${activos.length} activos filtrados`">
            <i class="fas fa-tags me-1"></i>
            {{ imprimiendoEtiquetas ? 'Generando...' : `Etiquetas (${activos.length})` }}
          </button>
        </div>
      </div>

      <div class="card-header card-header-custom pb-0">

        <div class="row g-2 mb-2">
          <div class="col-md-3">
            <label class="label-custom">Búsqueda General</label>
            <input type="text" class="form-control form-control-sm" placeholder="Nombre, serie, código..."
              v-model="filtros.q" @keyup.enter="buscar">
          </div>

          <div class="col-md-3">
            <label class="label-custom">¿Tiene Cód. Actual?</label>
            <select class="form-select form-select-sm" v-model="filtros.tiene_tamep" @change="buscar">
              <option value="">Todos (Con o sin código)</option>
              <option value="true">Sí (Tienen Cód. Actual)</option>
              <option value="false">No (Falta Cód. Actual)</option>
            </select>
          </div>

          <div class="col-md-3">
            <label class="label-custom">¿Tiene Cód. Histórico?</label>
            <select class="form-select form-select-sm" v-model="filtros.tiene_tab" @change="buscar">
              <option value="">Todos (Con o sin código)</option>
              <option value="true">Sí (Tienen Cód. Histórico)</option>
              <option value="false">No (Falta Cód. Histórico)</option>
            </select>
          </div>

          <div class="col-md-3 d-flex align-items-end gap-2">
            <button class="btn btn-sm mb-0 text-white shadow-sm font-weight-bolder flex-fill" style="background:#003366;" @click="buscar">
              <i class="fas fa-search me-1"></i> Buscar
            </button>
            <button class="btn btn-sm btn-outline-secondary mb-0 flex-fill" @click="limpiarFiltros">
              <i class="fas fa-eraser me-1"></i> Limpiar
            </button>
          </div>
        </div>

        <!-- ORGANIZATIVO (JERARQUICO) -->
        <div class="row g-2 mb-2 p-2 bg-gray-100 border-radius-md" style="border: 1px dashed #cbd5e1;">
          <div class="col-md-2">
            <label class="label-custom">Regional</label>
            <select class="form-select form-select-sm" v-model="filtros.regional">
              <option value="">Todas</option>
              <option v-for="r in regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="label-custom">Gerencia</label>
            <select class="form-select form-select-sm" v-model="filtros.gerencia" :disabled="!filtros.regional">
              <option value="">Todas las gerencias</option>
              <option v-for="g in opciones.gerencias" :key="g.id" :value="g.id">{{ g.nombre }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="label-custom text-info">Sección/Área</label>
            <select class="form-select form-select-sm border-info-light" v-model="filtros.seccion"
              :disabled="!filtros.gerencia">
              <option value="">Todas</option>
              <option v-for="s in opciones.secciones" :key="s.id" :value="s.id">{{ s.nombre }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="label-custom">Cargo</label>
            <select class="form-select form-select-sm" v-model="filtros.cargo" :disabled="!filtros.seccion">
              <option value="">Todos</option>
              <option v-for="c in opciones.cargos" :key="c.id" :value="c.id">{{ c.nombre }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="label-custom">Custodio</label>
            <select class="form-select form-select-sm" v-model="filtros.usuario" :disabled="!filtros.cargo">
              <option value="">Cualquiera</option>
              <option v-for="u in opciones.usuarios" :key="u.id" :value="u.id">{{ u.nombre_completo }}</option>
            </select>
          </div>
        </div>

        <div class="row g-2">
          <div class="col-md-3">
            <select class="form-select form-select-sm" v-model="filtros.grupo_contable" @change="buscar">
              <option value="">Todos los grupos</option>
              <option v-for="g in gruposContables" :key="g.id" :value="g.id">{{ g.nombre }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <select class="form-select form-select-sm" v-model="filtros.tipo_activo" :disabled="!filtros.grupo_contable"
              @change="buscar">
              <option value="">Todos los tipos</option>
              <option v-for="t in tiposActivo" :key="t.id" :value="t.id">{{ t.nombre }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <select class="form-select form-select-sm" v-model="filtros.situacion" @change="buscar">
              <option value="">Cualquier Situación</option>
              <option value="Disponible">Disponible</option>
              <option value="Asignado">Asignado</option>
              <option value="Mantenimiento">Mantenimiento</option>
              <option value="Baja">Baja</option>
            </select>
          </div>
          <div class="col-md-2">
            <select class="form-select form-select-sm" v-model="filtros.estado" @change="buscar">
              <option value="">Cualquier Estado</option>
              <option value="Bueno">Bueno</option>
              <option value="Regular">Regular</option>
              <option value="Malo">Malo</option>
            </select>
          </div>
        </div>

        <p class="text-xs text-secondary mt-3 mb-0" v-if="!loading && busquedaRealizada">
          <strong>{{ totalResultados }}</strong> activos encontrados
        </p>
      </div>

      <div class="card-body bg-section-custom px-0 pt-0 pb-2">
        <!-- ESTADO INICIAL: SIN BUSQUEDA REALIZADA -->
        <div v-if="!busquedaRealizada" class="text-center py-5 px-3">
          <div class="mb-3">
            <i class="fas fa-filter text-secondary opacity-3" style="font-size: 3.5rem;"></i>
          </div>
          <h6 class="font-weight-bolder text-dark mb-1">Centro de Consulta y Reportes</h6>
          <p class="text-xs text-secondary max-width-500 mx-auto mb-0" style="max-width: 500px;">
            Por favor, seleccione los filtros de búsqueda en el panel superior (Regional, Gerencia, Grupo Contable, etc.) y haga clic en <strong>Buscar</strong> para consultar la información.
          </p>
        </div>

        <!-- RESULTADOS DE LA BUSQUEDA -->
        <div v-else class="table-responsive p-0 mt-3">
          <table class="table align-items-center mb-0">
            <thead class="bg-gray-100">
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Activo / Códigos</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Regional / Ubicación</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Gerencia / Sección</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Custodio</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">Situación</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">Estado Físico</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="activos.length === 0">
                <td colspan="7" class="text-center py-4 text-muted">
                  No se encontraron activos con los filtros aplicados.
                </td>
              </tr>
              <tr v-for="a in activos" :key="a.id" class="border-bottom hover-row" v-else>
                <td class="ps-4">
                  <div class="d-flex py-1">
                    <div class="d-flex flex-column justify-content-center">
                      <h6 class="mb-0 text-sm font-weight-bold">{{ a.nombre_especifico }}</h6>
                      <p class="text-xs font-weight-bold text-azul-tamep mb-0">
                        CÓD. ACTUAL: {{ a.codigo_tamep || 'S/C' }}
                      </p>
                      <span class="text-xxs text-secondary">
                        CÓD. HISTÓRICO: {{ a.codigo_tab || 'Sin registrar' }}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="text-xs font-weight-bold text-dark">{{ a.regional_nombre || 'Sin Regional' }}</span>
                    <span class="text-xxs text-secondary">{{ a.ubicacion_nombre || 'Sin Ubicación' }}</span>
                  </div>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="text-xs font-weight-bold text-dark">{{ a.gerencia_nombre || 'Sin Gerencia' }}</span>
                    <span class="text-xxs text-secondary">{{ a.seccion_nombre || 'Sin Sección' }}</span>
                  </div>
                </td>
                <td>
                  <div class="d-flex flex-column">
                    <span class="text-xs font-weight-bold text-dark">{{ a.custodio_nombre || 'Sin asignar' }}</span>
                    <span class="text-xxs text-secondary" v-if="a.cargo_nombre">{{ a.cargo_nombre }}</span>
                  </div>
                </td>
                <td class="text-center">
                  <span :class="['badge badge-sm', badgeSituacion(a.situacion_activo)]">
                    {{ a.situacion_activo }}
                  </span>
                </td>
                <td class="text-center">
                  <span class="text-xs font-weight-bold">{{ a.estado_actual }}</span>
                </td>
                <td class="align-middle text-center">
                  <router-link :to="`/activo/detalle/${a.id}`" class="btn btn-xs btn-outline-primary mb-0">
                    <i class="fas fa-eye me-1"></i> Ver
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-transition {
  transition: all 0.3s ease;
}

.header-tamep {
  background: linear-gradient(135deg, #003366 0%, #001d3d 100%);
}

.card-custom {
  background-color: #ffffff;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.card-header-custom {
  background-color: transparent;
}

.bg-section-custom {
  background-color: #f8f9fa;
}

.table td,
.table th {
  white-space: normal !important;
}

.label-custom {
  font-size: 0.7rem;
  font-weight: bold;
  color: #8392ab;
  text-transform: uppercase;
  margin-bottom: 4px;
  display: block;
}
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom,
.dark-mode .card-custom {
  background-color: #1e1e1e !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid #2d2d2d !important;
}

.dark-version .bg-section-custom,
.dark-mode .bg-section-custom {
  background-color: #121212 !important;
}

.dark-version .card-header-custom,
.dark-mode .card-header-custom {
  border-bottom: 1px solid #333 !important;
}

.dark-version .bg-gray-100,
.dark-mode .bg-gray-100 {
  background-color: #2a2a2a !important;
  border: 1px solid #333 !important;
}

.dark-version .bg-gray-200,
.dark-mode .bg-gray-200 {
  background-color: #383a3f !important;
  color: #ffffff !important;
}

.dark-version .bg-gray-200 .text-dark,
.dark-mode .bg-gray-200 .text-dark {
  color: #eeeeee !important;
}

.dark-version .table,
.dark-mode .table {
  color: #e0e0e0 !important;
}

.dark-version .table thead th,
.dark-mode .table thead th {
  background-color: #2a2a2a !important;
  color: #ffffff !important;
  border-bottom: 1px solid #444 !important;
}

.dark-version .table td,
.dark-mode .table td {
  border-bottom: 1px solid #333 !important;
}

.dark-version .form-control,
.dark-mode .form-control {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.dark-version .form-control:focus,
.dark-mode .form-control:focus {
  border-color: #4dabf7 !important;
}

.dark-version .form-select,
.dark-mode .form-select {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.dark-version .form-select:focus,
.dark-mode .form-select:focus {
  border-color: #4dabf7 !important;
}

.dark-version .text-secondary,
.dark-mode .text-secondary {
  color: #aaaaaa !important;
}
</style>